import type { ReactElement } from 'react';
import styled from '@emotion/styled';
import type { IconProps } from '@hotjar/hotsauce';
import { Icon, Link, Text, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import COLLECTING_ICON from '@hotjar/shared-assets/images/icons/icon-status-running.gif';
import NOT_COLLECTING_ICON from '@hotjar/shared-assets/images/icons/icon-status-warning.gif';

type StateType = 'collecting' | 'notCollecting' | 'notInstalled';

interface StateOptions {
  textKey: string;
  action: 'view' | 'install';
  img?: string;
  icon?: Pick<IconProps, 'color' | 'icon'>;
}

interface TrackingCodeStateProps {
  lastData: number | null;
  isMobile: boolean;
  onClick(action: 'view' | 'install'): void;
}

const STATES: Record<StateType, StateOptions> = {
  collecting: {
    textKey: 'states.collecting',
    action: 'view',
    img: COLLECTING_ICON,
  },
  notCollecting: {
    textKey: 'states.not-collecting',
    action: 'view',
    img: NOT_COLLECTING_ICON,
  },
  notInstalled: {
    textKey: 'states.not-installed',
    action: 'install',
    icon: { icon: 'warning_alt', color: 'text_error' },
  },
};

const TRANSLATION_DOMAIN = 'account:sites-and-organizations.site.tracking';

const S = {
  Info: styled.div`
    display: flex;
    align-items: center;
  `,
  Image: styled.img`
    width: ${tokens.spacer.x2};
    margin-right: ${tokens.spacer.x1};
  `,
  Icon: styled(Icon)`
    margin-right: ${tokens.spacer.x1};
  `,
  Link: styled(Link)`
    ${tokens.text('caption')};
    color: ${tokens.color.text_light};
    margin-top: ${tokens.spacer.x0};
    cursor: pointer;
  `,
};

const getStateKey = (installed: boolean, active: boolean): StateType => {
  if (!installed) {
    return 'notInstalled';
  }
  return active ? 'collecting' : 'notCollecting';
};

export const TrackingCodeState = ({
  lastData,
  isMobile,
  onClick,
}: TrackingCodeStateProps): ReactElement => {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);

  const { getState } = useTrackingCode();
  const { installed, isActive } = getState(lastData);

  const key = getStateKey(installed, isActive);
  const { textKey, action, icon, img } = STATES[key];

  const visualIndicator = (
    <>
      {img && <S.Image src={img} alt={t(textKey)} />}
      {icon && <S.Icon description={t(textKey)} icon={icon.icon} color={icon.color} size="m" />}
    </>
  );

  return isMobile ? (
    <S.Info onClick={() => onClick(action)}>{visualIndicator}</S.Info>
  ) : (
    <>
      <S.Info>
        {visualIndicator}
        <Text>{t(textKey)}</Text>
      </S.Info>
      <S.Link onClick={() => onClick(action)} kind="inline">
        {t(action)}
      </S.Link>
    </>
  );
};
